import React from 'react';
import urllib from 'url-lib';

import { getCachedResource } from '@eb/resource-cache';
import { makeCachedResourceTools } from '@eb/resource-cache-tools';
import CalendarSvg from 'eventbrite_design_system/iconography/icons/Calendar';
import MapSvg from 'eventbrite_design_system/iconography/icons/Map';
import MoneySvg from 'eventbrite_design_system/iconography/icons/Money';

import gettext from '@eb/gettext';
import { joinPath } from '@eb/path-utils';
import lazyGettext from 'js-utils/lazy-gettext';
import {
    sdkRequest,
    translateArgumentErrors,
    translateServerErrors,
} from '@eb/http';

import {
    EVENT_API_GET_EXPANSIONS,
    SCHEDULE_PUBLISH_SCHEDULED,
    SCHEDULE_PUBLISH_STATUS,
    SCHEDULE_PUBLISH_DATE,
    STATE_RESOURCE_CACHE_NAME,
} from '../constants';

const ERROR_PUBLISH_WITHOUT_TICKET = 'error-publish-without-ticket';
const ERROR_PUBLISH_WITHOUT_SEATMAP = 'error-publish-without-seatmap';
const ERROR_CANNOT_PUBLISH_SERIES_WITH_NO_DATES =
    'error-cannot-publish-series-with-no-dates';
const ERROR_CANNOT_PUBLISH_INVALID_PAYMENT_TYPE =
    'error-cannot-publish-invalid-payment-type';
const ERROR_PUBLISH_DEFAULT = 'error-publish-default';
const ERROR_PUBLISH_PUBLISHED_OR_DELETED = 'error-publish-published-or-deleted';
const ERROR_PUBLISH_NO_CHECKOUT_SETTINGS = 'error-publish-no-checkout-settings';
const ERROR_PUBLISH_NO_PAYPAL_EMAIL = 'error-publish-no-paypal-email';
const ERROR_PUBLISH_TOO_SOON = 'error-publish-too-soon';
const ERROR_UNPUBLISH_NOT_PUBLISHED = 'error-unpublish-not-published';
const ERROR_UNPUBLISH_REFUND = 'error-unpublish-refund';
const ERROR_UNPUBLISH_DEFAULT = 'error-unpublish-default';
const ERROR_CANCEL_CANCELED = 'error-cancel-canceled';
export const ERROR_CANCEL_REFUND = 'error-cancel-refund';
export const ERROR_CANCEL_REFUND_WITH_BULK_ENABLED =
    'error-cancel-refund-with-bulk-enabled';
const ERROR_CANCEL_DEFAULT = 'error-cancel-default';
const ERROR_DELETE_DELETED = 'error-delete-deleted';
export const ERROR_DELETE_REFUND = 'error-delete-refund';
const ERROR_DELETE_DEFAULT = 'error-delete-default';
const ERROR_CANCEL_SCHEDULED_PUBLISH = 'error-cancel-scheduled-publish';
const ERROR_NOT_AUTHORIZED = 'error-not-authorized';
const ERROR_DEFAULT = 'error-default';
const ERROR_SAVE_SALES_STATUS_DEFAULT = 'error-save-sales-status-default';
const ERROR_SALE_STATUS_MAX_LENGTH_EXCEEDED =
    'error-sales-status-max-length-exceeded';
const ERROR_END_ONLINE_SALES_DEFAULT = 'error-end-online-sales-default';
const ERROR_PUBLISH_WITHOUT_TAX_SETTINGS = 'error-publish-without-tax-settings';

const ERROR_MESSAGES_MAP = {
    [ERROR_NOT_AUTHORIZED]: {
        title: gettext('You do not have permission to complete this action.'),
        body: () =>
            gettext(
                'Please contact your account administrator to request access.',
            ),
    },
    [ERROR_PUBLISH_WITHOUT_TICKET]: {
        title: gettext('Create a ticket to publish'),
        body: () =>
            gettext(
                "You haven't created any tickets yet. At least one ticket is required to publish your event.",
            ),
        getSecondaryActionLink: (baseUrl, eventId) =>
            joinPath(baseUrl, eventId, 'tickets'),
    },
    [ERROR_PUBLISH_WITHOUT_SEATMAP]: {
        title: gettext('Complete your map to publish'),
        body: () =>
            gettext(
                'We were unable to publish your event because your seat map is incomplete. Check that all of your seats have been painted and that everything has labels, then try again.',
            ),
        getSecondaryActionLink: (baseUrl, eventId) =>
            `/create/seat-map?eid=${eventId}`,
        secondaryActionText: gettext('Edit Venue Map'),
        isActionLinkExternal: true,
        icon: <MapSvg />,
    },
    [ERROR_CANNOT_PUBLISH_SERIES_WITH_NO_DATES]: {
        title: gettext(
            'You must have at least one date scheduled to publish the series.',
        ),
        body: () =>
            gettext('Recurring events require at least one scheduled date.'),
        getSecondaryActionLink: (baseUrl, eventId) =>
            `/manage/events/${eventId}/schedule`,
        secondaryActionText: gettext('Reschedule'),
        isActionLinkExternal: true,
        icon: <CalendarSvg />,
    },
    [ERROR_CANNOT_PUBLISH_INVALID_PAYMENT_TYPE]: {
        title: gettext(
            'You must have valid Payment Options to publish this event.',
        ),
        body: () =>
            gettext(
                'Please update your Country & Currency combination before trying to publish your event.',
            ),
        getSecondaryActionLink: (baseUrl, eventId) =>
            `/manage/events/${eventId}/checkout`,
        secondaryActionText: gettext('Edit Payment Options'),
        isActionLinkExternal: true,
        icon: <MoneySvg />,
    },
    [ERROR_PUBLISH_DEFAULT]: {
        title: gettext('Unable to publish event'),
        body: () =>
            gettext(
                'There was an error publishing your event. Please try again.',
            ),
    },

    [ERROR_PUBLISH_PUBLISHED_OR_DELETED]: {
        title: gettext('Unable to publish event'),
        body: () => gettext('Event is already published or deleted.'),
    },

    [ERROR_PUBLISH_NO_CHECKOUT_SETTINGS]: {
        title: gettext('Select a payout country to publish'),
        body: () =>
            gettext(
                "You haven't selected a payout country or currency for your event.  This information is required to publish.",
            ),
        getSecondaryActionLink: (baseUrl, eventId) =>
            joinPath(baseUrl, eventId, 'checkout'),
    },

    [ERROR_PUBLISH_NO_PAYPAL_EMAIL]: {
        title: gettext('Select a PayPal email to publish'),
        body: () =>
            gettext(
                "You haven't selected a PayPal email address for your event.  This information is required to publish.",
            ),
    },

    [ERROR_PUBLISH_TOO_SOON]: {
        title: gettext('Unable to publish event'),
        body: () =>
            gettext(
                'The publish date should be at least 10 minutes into the future.',
            ),
    },

    [ERROR_UNPUBLISH_NOT_PUBLISHED]: {
        title: gettext('Unable to unpublish event'),
        body: () => gettext('This event is not currently published.'),
    },

    [ERROR_UNPUBLISH_REFUND]: {
        title: gettext('Refund orders to unpublish'),
        body: () =>
            gettext(
                'Attendees have ordered tickets for your event. Refund or cancel all orders to unpublish your event.',
            ),
    },

    [ERROR_UNPUBLISH_DEFAULT]: {
        title: gettext('Unable to unpublish your event'),
        body: () =>
            gettext(
                'There was an error unpublishing your event. Please try again.',
            ),
    },

    [ERROR_CANCEL_CANCELED]: {
        title: gettext('Unable to cancel your event'),
        body: () => gettext('This event is already cancelled.'),
    },

    [ERROR_CANCEL_REFUND]: {
        title: gettext('Attendees already registered'),
        body: () =>
            lazyGettext(
                'Before canceling this event, you\'ll need to cancel all existing orders and issue refunds, if necessary. <a href="/support/articles/en_US/Multi_Group_How_To/how-to-cancel-an-event?lg=en_US" target="_blank">Learn more about canceling an event</a>',
            ),
        bodyContainsHtml: true,
        getPrimaryActionLink: (baseUrl, eventId) =>
            `/reports?eid=${eventId}&rid=h&filterby=attending,all`,
        primaryActionText: gettext('View Orders'),
        isActionLinkExternal: true,
    },
    // [TECH-DEBT] - EB-139434: This will be deleted once the bulk refunds rampup is done
    [ERROR_CANCEL_REFUND_WITH_BULK_ENABLED]: {
        title: gettext('You have registered attendees'),
        body: ({ eventId }) =>
            lazyGettext(
                `In order to cancel this event, you\'ll need to cancel all orders and refund your attendees.
                 You can <a href=/manage/events/%(eventId)s/refund_process target=_blank>refund multiple attendees at once</a>,
                 or issue refunds individually from the Orders page.
                 <a href=/support/articles/en_US/Multi_Group_How_To/how-to-cancel-an-event?lg=en_US target=_blank>Learn more about canceling an event</a>`,
                { eventId },
            ),
        bodyContainsHtml: true,
        getPrimaryActionLink: (baseUrl, eventId) =>
            `/reports?eid=${eventId}&rid=h&filterby=attending,all`,
        primaryActionText: gettext('View Orders'),
        isActionLinkExternal: true,
    },

    [ERROR_CANCEL_DEFAULT]: {
        title: gettext('Unable to cancel your event'),
        body: () =>
            gettext(
                'There was an error canceling your event. Please try again.',
            ),
    },

    [ERROR_DELETE_DELETED]: {
        title: gettext('Unable to delete your event'),
        body: () => gettext('This event is already deleted.'),
    },

    [ERROR_DELETE_REFUND]: {
        title: gettext('Refund orders to delete'),
        body: () =>
            gettext(
                'Attendees have ordered tickets for your event. Refund or cancel all orders to delete your event.',
            ),
    },

    [ERROR_DELETE_DEFAULT]: {
        title: gettext('Unable to delete your event'),
        body: () =>
            gettext(
                'There was an error deleting your event. Please try again.',
            ),
    },
    [ERROR_CANCEL_SCHEDULED_PUBLISH]: {
        title: gettext('Unable to cancel your scheduled publish date.'),
        body: () =>
            gettext(
                'There was an error cancelling your scheduled publish date. Please try again.',
            ),
    },
    [ERROR_DEFAULT]: {
        title: gettext('Unable to perform the action'),
        body: () => gettext('There was an error. Please try again.'),
    },

    [ERROR_SALE_STATUS_MAX_LENGTH_EXCEEDED]: {
        title: gettext('Unable to change status'),
        body: () =>
            gettext(
                'There was an error changing the status. Message length exceeds the max length allowed.',
            ),
    },
    [ERROR_SAVE_SALES_STATUS_DEFAULT]: {
        title: gettext('Unable to change status'),
        body: () =>
            gettext(
                'There was an error changing the event status. Please try again.',
            ),
    },

    [ERROR_END_ONLINE_SALES_DEFAULT]: {
        title: gettext('Unable to end online sales'),
        body: () =>
            gettext(
                'There was an error ending the online sales. Please try again.',
            ),
    },

    [ERROR_PUBLISH_WITHOUT_TAX_SETTINGS]: {
        title: gettext('Fill out your tax settings'),
        body: () =>
            gettext(
                'You must complete your tax settings before publishing your event.',
            ),
        getSecondaryActionLink: (baseUrl, eventId) =>
            joinPath(baseUrl, eventId, 'tax'),
        icon: <MoneySvg />,
    },
};

export const getEventActionErrorDetail = (actionError) =>
    ERROR_MESSAGES_MAP[actionError] || ERROR_MESSAGES_MAP[ERROR_DEFAULT];

/**
 * Translate possible publish server errors into meaningful messages
 */
const _publishErrorSpec = {
    ARGUMENTS_ERROR: translateArgumentErrors({
        'event.tickets': {
            default: () => ERROR_PUBLISH_WITHOUT_TICKET,
        },
        'event.seatmap': {
            default: () => ERROR_PUBLISH_WITHOUT_SEATMAP,
        },
        'event.tax_settings': {
            default: () => ERROR_PUBLISH_WITHOUT_TAX_SETTINGS,
        },
        default: () => ERROR_PUBLISH_DEFAULT,
    }),

    ALREADY_PUBLISHED_OR_DELETED: () => ERROR_PUBLISH_PUBLISHED_OR_DELETED,
    PAYMENT_OPTIONS_NO_PAYMENT_TYPE: () => ERROR_PUBLISH_NO_CHECKOUT_SETTINGS,
    NO_PAYMENT_OPTIONS: () => ERROR_PUBLISH_NO_CHECKOUT_SETTINGS,
    PAYMENT_OPTIONS_PAYPAL_NO_EMAIL: () => ERROR_PUBLISH_NO_PAYPAL_EMAIL,
    ERROR_CANNOT_PUBLISH_SERIES_WITH_NO_DATES: () =>
        ERROR_CANNOT_PUBLISH_SERIES_WITH_NO_DATES,
    ERROR_CANNOT_PUBLISH_INVALID_PAYMENT_TYPE: () =>
        ERROR_CANNOT_PUBLISH_INVALID_PAYMENT_TYPE,
    NOT_AUTHORIZED: () => ERROR_NOT_AUTHORIZED,
    default: () => ERROR_PUBLISH_DEFAULT,
};

/**
 * Publish an event so that all may gaze upon its magnificence, provided they
 * have the necessary permissions.
 *
 * @param eventId
 * @returns {Promise}
 */
export const publishEvent = (eventId) =>
    sdkRequest(`/api/v3/events/${eventId}/publish/`, {
        method: 'POST',
    }).catch(translateServerErrors(_publishErrorSpec));

/**
 * Translate possible unpublish server errors into meaningful messages
 */
const _unpublishErrorSpec = {
    NOT_PUBLISHED: () => ERROR_UNPUBLISH_NOT_PUBLISHED,
    CANNOT_UNPUBLISH: () => ERROR_UNPUBLISH_REFUND,
    NOT_AUTHORIZED: () => ERROR_NOT_AUTHORIZED,
    default: () => ERROR_UNPUBLISH_DEFAULT,
};

/**
 * Possible scheduled publish errors coming back from publish_settings update.
 */
export const schedulePublishErrorSpec = {
    ARGUMENTS_ERROR: translateArgumentErrors({
        'publish_settings.schedule_publish_date': {
            SCHEDULE_PUBLISH_TOO_SOON: () => ERROR_PUBLISH_TOO_SOON,
            default: () => ERROR_PUBLISH_DEFAULT,
        },
        'event.tax_settings': {
            default: () => ERROR_PUBLISH_WITHOUT_TAX_SETTINGS,
        },
    }),
    ERROR_CANNOT_PUBLISH_INVALID_PAYMENT_TYPE: () =>
        ERROR_CANNOT_PUBLISH_INVALID_PAYMENT_TYPE,
    NOT_AUTHORIZED: () => ERROR_NOT_AUTHORIZED,
    NOT_FOUND: () => ERROR_PUBLISH_DEFAULT,
    default: () => ERROR_PUBLISH_DEFAULT,
};

/**
 * Take this event and squirrel it away from the prying eyes of the public.
 *
 * @param eventId
 * @returns {Promise}
 */
export const unpublishEvent = (eventId) =>
    sdkRequest(`/api/v3/events/${eventId}/unpublish/`, {
        method: 'POST',
    }).catch(translateServerErrors(_unpublishErrorSpec));

/**
 * Translate possible cancel server errors into meaningful messages
 */
const _cancelErrorSpec = {
    ALREADY_CANCELED: () => ERROR_CANCEL_CANCELED,
    CANNOT_CANCEL: () => ERROR_CANCEL_REFUND,
    NOT_AUTHORIZED: () => ERROR_NOT_AUTHORIZED,
    default: () => ERROR_CANCEL_DEFAULT,
};

/**
 * Take this event and squirrel it away from the prying eyes of the public.
 *
 * @param eventId
 * @returns {Promise}
 */
export const cancelEvent = (eventId) =>
    sdkRequest(`/api/v3/events/${eventId}/cancel/`, {
        method: 'POST',
    }).catch(translateServerErrors(_cancelErrorSpec));

/**
 * Translate possible delete server errors into meaningful messages
 */
const _deleteErrorSpec = {
    ALREADY_DELETED: () => ERROR_DELETE_DELETED,
    CANNOT_DELETE: () => ERROR_DELETE_REFUND,
    NOT_AUTHORIZED: () => ERROR_NOT_AUTHORIZED,
    default: () => ERROR_DELETE_DEFAULT,
};

/**
 * Delete this event.
 *
 * @param eventId
 * @returns {Promise}
 */
export const deleteEvent = (eventId) =>
    sdkRequest(`/api/v3/events/${eventId}/`, {
        method: 'DELETE',
    }).catch(translateServerErrors(_deleteErrorSpec));

export const cancelSchedulePublishErrorSpec = {
    NOT_AUTHORIZED: () => ERROR_NOT_AUTHORIZED,
    default: () => ERROR_CANCEL_SCHEDULED_PUBLISH,
};

/**
 * Return a `fetch` Promise to save event publish settings data
 *
 * @param {object} data - event publish settings data to save to the api
 * @param {object} errorSpec - error specification provided to `translateServerErrors` in the event of a failing
 *                             response
 * @param {string} eventId - the event to update
 * @returns {Promise}
 */
export const savePublishSettings = (data, errorSpec, eventId = null) => {
    if (!eventId) {
        throw new Error('Event Id is required to save publish settings.');
    }

    const url = `/api/v3/events/${eventId}/publish_settings/`;
    const options = {
        method: 'POST',
        body: JSON.stringify(data),
    };

    return sdkRequest(url, options).catch(translateServerErrors(errorSpec));
};

/**
 * Translate possible save sales status server errors into meaningful messages
 */
const _salesStatusErrorSpec = (eventTextKey) => {
    const argumentErrorKey = `texts.${eventTextKey}.message`;

    return {
        ARGUMENTS_ERROR: translateArgumentErrors({
            [argumentErrorKey]: {
                MAX_LENGTH_EXCEEDED: () =>
                    ERROR_SALE_STATUS_MAX_LENGTH_EXCEEDED,
                default: () => ERROR_SAVE_SALES_STATUS_DEFAULT,
            },
        }),
        NOT_AUTHORIZED: () => ERROR_NOT_AUTHORIZED,
        default: () => ERROR_SAVE_SALES_STATUS_DEFAULT,
    };
};

export const saveSalesStatus = (data, eventId, eventTextKey) => {
    const url = `/api/v3/events/${eventId}/texts/`;
    const options = {
        method: 'POST',
        body: JSON.stringify(data),
    };

    return sdkRequest(url, options).catch(
        translateServerErrors(_salesStatusErrorSpec(eventTextKey)),
    );
};

/**
 * Translate possible end online sales server errors into meaningful messages.
 * We use default http labels as redux-form always catches the request to end online
 * sales, and we have to manually check for the errors later.
 */
export const endOnlineSalesErrorSpec = {
    FORBIDDEN: ERROR_NOT_AUTHORIZED,
    DEFAULT: ERROR_END_ONLINE_SALES_DEFAULT,
};

export const endOnlineSales = (eventId) =>
    sdkRequest(`/api/v3/events/${eventId}/end_online_sales/`, {
        method: 'POST',
    }).catch((response) => Promise.resolve(response));

export const eventCache = makeCachedResourceTools(({ eventId }) =>
    urllib.formatUrl(`/api/v3/events/${eventId}/`, {
        expand: EVENT_API_GET_EXPANSIONS.join(','),
    }),
);

/**
 * Return a string to use for storing and retrieving event publish settings from resourceCache
 * @param eventId - the public id of the event
 * @returns {string}
 */
export const getPublishSettingsCacheKey = (eventId) =>
    `publishSettings/${eventId}`;

/**
 * Return a `fetch` Promise to load event publish settings
 *
 * @param eventId - the public id of the event to ask for.
 * @returns {Promise}
 */
export const getPublishSettings = (eventId) =>
    sdkRequest(`/api/v3/events/${eventId}/publish_settings/`);
